import { Card, CardProps } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { AutocompleteField } from '.';
import { GooglePlaceItem, useGooglePlaces, UseGooglePlacesConfig } from '../../hooks';
import { type PlaceDetails } from '../../hooks/useGooglePlaces';
import { parseGqlError, TranslationFunction } from '../../utils';
import { AutocompleteOption } from '../DefaultOption';
import { GoogleOfficialIcon, SearchIcon } from '../icons';
import { AutocompleteFieldProps } from './AutocompleteField';

const styleBox = {
  borderRadius: 'lg',
  w: 'full',
  bg: 'bg.light',
  px: 'md',
  pt: 'md',
};

type OptionWithDetails = {
  label: string;
  value: string;
  details: PlaceDetails;
};

interface BusinessSearchFieldProps extends Omit<CardProps, 'onChange'> {
  t?: TranslationFunction;
  label?: string;
  helperText?: string;
  onChange?: (data: OptionWithDetails | null) => void;
  name: string;
  isRequired?: boolean;
  placeholder?: string;
  googlePlacesConfig: UseGooglePlacesConfig;
  getBusinessData?: boolean;
  inputProps?: AutocompleteFieldProps;
}

const BusinessSearchField: React.FC<BusinessSearchFieldProps> = ({
  t,
  label,
  helperText,
  onChange,
  name,
  isRequired,
  googlePlacesConfig,
  placeholder,
  getBusinessData = true,
  inputProps,
  ...style
}) => {
  const { control, setError, clearErrors } = useFormContext();

  const { getGooglePlaceID, getPlaceDetails } = useGooglePlaces(googlePlacesConfig);

  const handleSearch = async (value: string) => {
    try {
      const data = await getGooglePlaceID({
        input: value,
      });

      clearErrors(name);

      return data
        ? data
            .filter(({ place_id }) => place_id.startsWith('ChIJ'))
            .map((item: GooglePlaceItem) => {
              return {
                label: item.description,
                value: item.place_id,
              };
            })
        : [];
    } catch (error) {
      const parseError = t ? parseGqlError(error as any, t) : { message: 'Business not found' };
      setError(name, { type: 'custom', message: parseError.message });
      return [];
    }
  };

  const handleSelect = async (option: AutocompleteOption | null) => {
    if (option && option.value && getBusinessData) {
      const details = await getPlaceDetails({
        placeId: option.value,
        fields: [
          'addressComponents',
          'generativeSummary',
          'nationalPhoneNumber',
          'formattedAddress',
          'displayName',
          'websiteUri',
          'editorialSummary',
          'types',
          'regularOpeningHours',
          'utcOffsetMinutes',
          'googleMapsUri',
        ],
      });
      onChange?.({
        ...option,
        details,
      });
    } else {
      onChange?.(null);
    }
  };

  return label ? (
    <Card
      {...styleBox}
      {...style}
    >
      <AutocompleteField
        control={control}
        getOptions={handleSearch}
        isRequired={isRequired}
        isTruncated
        label={label}
        helperText={helperText}
        leftElement={
          <SearchIcon
            color='primary.pure'
            boxSize={5}
          />
        }
        name={name}
        placeholder={
          placeholder ||
          (t ? t('location.google-place-input-placeholder') : 'Enter business name or address')
        }
        rightElement={<GoogleOfficialIcon boxSize={4} />}
        onChange={handleSelect}
        pr='4rem'
        bg='white'
        {...inputProps}
      />
    </Card>
  ) : (
    <AutocompleteField
      control={control}
      getOptions={handleSearch}
      isRequired={isRequired}
      isTruncated
      leftElement={
        <SearchIcon
          color='primary.pure'
          boxSize={5}
        />
      }
      name={name}
      placeholder={
        placeholder ||
        (t ? t('location.google-place-input-placeholder') : 'Enter business name or address')
      }
      rightElement={<GoogleOfficialIcon boxSize={4} />}
      onChange={handleSelect}
      pr='4rem'
      {...inputProps}
    />
  );
};

export default BusinessSearchField;
